export type NavItem = {
  /** The label, as shown in the nav. */
  label: string;
  /** The link to the page used in the nav. Often used if the nav redirects to another existing page. If not provided, the path will be used. */
  href?: string;
  /** The path to the mdx page. */
  path?: string;
  /** The sub-items in the nav. */
  nav?: NavItem[];
  /**
   * Private nav items are only shown to logged in users, and are excluded from search.
   *
   * IMPORTANT: When adding a private page
   *    1. include it in `excludedSearchPages` in `scripts/update-algolia-index.mjs` to exclude from algolia search results.
   *    2. include it in `noIndexPages` in `src/util/no-index-pages.js` to mark as noIndex and exclude from sitemap.
   */
  private?: boolean;
};

/**
 * ! IMPORTANT !
 * When adding a new item to the root level of the config, you must also include it in:
 * `roots` in `scripts/update-algolia-index.mjs`
 * to ensure it is indexed by algolia.
 *
 * When marking a page as private,
 *    1. include it in `excludedSearchPages` in `scripts/update-algolia-index.mjs` to exclude from algolia search results.
 *    2. include it in `noIndexPages` in `src/util/no-index-pages.js` to mark as noIndex and exclude from sitemap.
 *
 * TODO: consolidate this split logic so it's not as convoluted. We can't directly import the config to update-algolia-index.mjs,
 * so we need to duplicate the logic here. Consider writing a script that could be used in update-algolia-index.mjs.
 */
export const config: NavItem[] = [
  {
    label: "Getting started",
    path: "/getting-started",
    nav: [
      {
        label: "Welcome",
        path: "/welcome",
        href: "/",
      },
      {
        label: "Core concepts",
        path: "/concepts",
      },
      {
        label: "Create your first sync",
        path: "/create-your-first-sync",
      },
    ],
  },
  {
    label: "Syncs",
    path: "/syncs",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Sync types and modes",
        path: "/types-and-modes",
      },
      {
        label: "Record matching",
        path: "/record-matching",
      },
      {
        label: "Field mapping",
        path: "/mapping-data",
      },
      {
        label: "Match boosting",
        path: "/match-boosting",
      },
      {
        label: "Sync schedules",
        nav: [
          {
            label: "Schedule syncs in the UI",
            path: "/schedule-sync-ui",
          },
          {
            label: "Schedule syncs with Sequences",
            path: "/schedule-sync-with-sequences",
          },
          {
            label: "Trigger syncs via dbt Cloud",
            href: "/extensions/dbt-cloud",
          },
          {
            label: "Trigger syncs via Fivetran",
            href: "/extensions/fivetran",
          },
          {
            label: "Trigger syncs via Airflow",
            href: "/extensions/airflow",
          },
          {
            label: "Trigger syncs via Dagster",
            href: "/extensions/dagster",
          },
          {
            label: "Trigger syncs via Prefect",
            href: "/extensions/prefect",
          },
          {
            label: "Trigger sync via API",
            href: "/developer-tools/api-guide",
          },
        ],
      },
      {
        label: "Troubleshooting syncs",
        nav: [
          {
            label: "Data types and casting",
            href: "/models/data-types-casting",
          },
          {
            label: "Live debugger",
            path: "/debugger",
          },
        ],
      },
      {
        label: "Additional features",
        nav: [
          {
            label: "Retry strategy",
            path: "/retries",
          },
          {
            label: "Alerting",
            path: "/alerting",
          },
          {
            label: "Lightning sync engine",
            path: "/lightning-sync-engine",
          },
          {
            label: "Warehouse sync logs",
            path: "/warehouse-sync-logs",
          },
          {
            label: "Data extraction",
            path: "/data-extraction",
          },
        ],
      },
    ],
  },
  {
    label: "Models",
    path: "/models",
    nav: [
      {
        label: "Overview",
        path: "/creating-models",
      },
      {
        label: "Data types and casting",
        path: "/data-types-casting",
      },
      {
        label: "Match boosting",
        path: "/match-boosting",
      },
      {
        label: "Modeling methods",
        nav: [
          {
            label: "SQL editor",
            path: "/sql-editor",
          },
          {
            label: "Table selector",
            path: "/table-selector",
          },
          {
            label: "dbt models and analyses",
            href: "/extensions/dbt-models",
          },
          {
            label: "Looker Looks",
            href: "/extensions/looker-models",
          },
          {
            label: "Sigma workbooks",
            href: "/extensions/sigma",
          },
        ],
      },
    ],
  },
  {
    label: "Hightouch Events",
    path: "/events",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Event tracking spec",
        path: "/event-spec",
      },
      {
        label: "Event streaming",
        path: "/event-streaming",
      },
      {
        label: "First party tracking",
        path: "/first-party-tracking",
      },
      {
        label: "Event Sources",
        nav: [
          {
            label: "Browser (Javascript)",
            path: "/sdks/browser",
          },
          {
            label: "Node.js",
            path: "/sdks/nodejs",
          },
          {
            label: "Ruby",
            path: "/sdks/ruby",
          },
          {
            label: "Go",
            path: "/sdks/go",
          },
          {
            label: "PHP",
            path: "/sdks/php",
          },
          {
            label: "Java",
            path: "/sdks/java",
          },
          {
            label: "Python",
            path: "/sdks/python",
          },
          {
            label: "iOS",
            path: "/sdks/ios",
          },
          {
            label: "Android",
            path: "/sdks/android",
          },
          {
            label: "React Native",
            path: "/sdks/react-native",
          },
          {
            label: "Flutter",
            path: "/sdks/flutter",
          },
          {
            label: "C# (.NET)",
            path: "/sdks/csharp",
          },
          {
            label: "HTTP",
            path: "/sdks/http",
          },
          {
            label: "Webhook",
            path: "/sources/webhook",
          },
          {
            label: "Kafka",
            path: "/sources/kafka",
          },
          {
            label: "Google PubSub",
            path: "/sources/google-pubsub",
          },
        ],
      },
      {
        label: "Warehouse destinations",
        nav: [
          {
            label: "Schema",
            path: "/warehouses/schema",
          },
          {
            label: "Error handling",
            path: "/warehouses/error-handling",
          },
        ],
      },
      {
        label: "Contracts",
        nav: [
          {
            label: "Managing contracts",
            path: "/contracts/management",
          },
          {
            label: "Handling violations",
            path: "/contracts/violations",
          },
          {
            label: "Managing via Git Sync",
            href: "/extensions/git-sync#event-contract-schema",
          },
        ],
      },
      {
        label: "Functions",
        nav: [
          {
            label: "Overview",
            path: "/functions/overview",
          },
          {
            label: "Getting started",
            path: "/functions/getting-started",
          },
          {
            label: "Handling webhooks",
            path: "/functions/handling-webhooks",
          },
        ],
      },
      {
        label: "Consent",
        nav: [
          {
            label: "Consent Manager",
            path: "/consent/consent-manager",
          },
          {
            label: "OneTrust",
            path: "/consent/onetrust",
          },
        ],
      },
      {
        label: "How to migrate to Hightouch Events",
        nav: [
          {
            label: "Overview",
            path: "/migration-guide/overview",
          },
          {
            label: "1. Preparing for migration",
            path: "/migration-guide/preparing-for-migration",
          },
          {
            label: "2. Implementing Hightouch Events SDKs",
            path: "/migration-guide/implementing-hightouch-events",
          },
          {
            label: "3. Defining data contracts for data governance",
            path: "/migration-guide/defining-data-contracts",
          },
          {
            label: "4. Validating your migration",
            path: "/migration-guide/validating-your-migration",
          },
          {
            label: "5. Unifying historical and new data",
            path: "/migration-guide/unifying-data",
          },
          {
            label: "6. Setting up monitoring and alerts",
            path: "/migration-guide/setting-up-alerts",
          },
          {
            label: "7. Updating downstream applications and destinations",
            path: "/migration-guide/updating-downstream-applications",
          },
        ],
      },
    ],
  },
  {
    label: "Identity Resolution",
    private: true,
    path: "/identity-resolution",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Usage",
        path: "/usage",
      },
      {
        label: "Golden Record",
        path: "/golden-record",
      },
      {
        label: "Configuration",
        nav: [
          {
            label: "Model configuration",
            path: "/model-configuration",
          },
          {
            label: "Identifier rules",
            path: "/identifier-rules",
          },
        ],
      },
    ],
  },
  {
    label: "Customer Studio",
    path: "/customer-studio",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Define data schema",
        path: "/schema",
      },
      {
        label: "Create audiences",
        path: "/usage",
      },
      {
        label: "Sync audiences",
        path: "/syncs",
      },
      {
        label: "Audience insights",
        path: "/insights",
      },
      {
        label: "Journeys",
        path: "/journeys",
      },
      {
        label: "Additional features",
        nav: [
          {
            label: "Traits",
            path: "/traits",
          },
          {
            label: "Splits",
            path: "/splits",
          },
          {
            label: "Holdout Group Logs",
            path: "/holdout-group-logs",
          },
          {
            label: "Priority Lists",
            path: "/priority-lists",
          },
          {
            label: "Subsets",
            path: "/subsets",
          },
          {
            label: "Destination Rules",
            path: "/destination-rules",
          },
          {
            label: "Snapshots",
            path: "/audience-snapshots",
          },
          {
            label: "Sampling",
            path: "/sampling",
          },
        ],
      },
    ],
  },
  {
    label: "AI Decisioning",
    private: true,
    path: "/ai-decisioning",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Security",
        path: "/security",
      },
      {
        label: "Technical setup",
        nav: [
          {
            label: "Configuration",
            path: "/configuration",
          },
          {
            label: "Prepare data for AID",
            path: "/prepare-data",
          },
        ],
      },
      {
        label: "Build your campaigns",
        nav: [
          {
            label: "Agents",
            path: "/agents",
          },
          {
            label: "Inspector",
            path: "/inspector",
          },
          {
            label: "Collections",
            path: "/collections",
          },
          {
            label: "Insights",
            path: "/insights",
          },
        ],
      },
      {
        label: "Destinations",
        nav: [
          {
            label: "Braze",
            path: "/braze",
          },
          {
            label: "Iterable",
            path: "/iterable",
          },
          {
            label: "SFMC",
            path: "/sfmc",
          },
        ],
      },
    ],
  },
  {
    label: "Campaign Intelligence",
    path: "/campaign-intelligence",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Campaigns",
        nav: [
          {
            label: "Ads dashboard",
            path: "/ads",
          },
          {
            label: "Emails dashboard",
            path: "/emails",
          },
        ],
      },
      {
        label: "Chart Types",
        nav: [
          {
            label: "Insights",
            path: "/insights",
          },
          {
            label: "Funnels",
            path: "/funnels",
          },
          {
            label: "Experiments",
            path: "/experiments",
          },
        ],
      },
      {
        label: "Measurement",
        nav: [
          {
            label: "Metrics",
            path: "/metrics",
          },
          {
            label: "Attribution methods",
            path: "/attribution-methods",
          },
        ],
      },
      {
        label: "AI",
        nav: [
          {
            label: "Copilot",
            path: "/copilot",
          },
        ],
      },

      {
        label: "Schema",
        nav: [
          {
            label: "Events",
            path: "/events",
          },
          {
            label: "Assets",
            path: "/assets",
          },
          {
            label: "Messages",
            path: "/schema/messages",
          },
          {
            label: "Ads",
            path: "/schema/ads",
          },
        ],
      },
      {
        label: "Definitions",
        nav: [
          {
            label: "Charts",
            path: "/charts",
          },
        ],
      },
    ],
  },
  {
    label: "Sources",
    path: "/sources",
  },
  {
    label: "Destinations",
    path: "/destinations",
  },
  {
    label: "Match Booster",
    path: "/match-booster",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Implementation",
        path: "/implementation",
      },
      {
        label: "Offerings",
        path: "/offerings",
      },
      {
        label: "FAQs",
        path: "/faqs",
      },
    ],
  },
  {
    label: "Extensions",
    path: "/extensions",
    nav: [
      {
        label: "Alerting and monitoring",
        nav: [
          {
            label: "Alerting overview",
            href: "/syncs/alerting",
          },
          {
            label: "PagerDuty",
            path: "/pagerduty",
          },
          {
            label: "Datadog",
            path: "/datadog",
          },
        ],
      },
      {
        label: "Model building",
        nav: [
          {
            label: "dbt models and analyses",
            path: "/dbt-models",
          },
          {
            label: "dbt Cloud models",
            path: "/dbt-cloud#dbt-cloud-model-sync",
          },
          {
            label: "Looker Looks",
            path: "/looker-models",
          },
          {
            label: "Sigma workbooks",
            path: "/sigma",
          },
        ],
      },
      {
        label: "Sync scheduling",
        nav: [
          {
            label: "dbt Cloud",
            path: "/dbt-cloud",
          },
          {
            label: "Fivetran",
            path: "/fivetran",
          },
          {
            label: "Airflow",
            path: "/airflow",
          },
          {
            label: "Dagster",
            path: "/dagster",
          },
          {
            label: "Prefect",
            path: "/prefect",
          },
          {
            label: "Mage",
            path: "/mage",
          },
        ],
      },
      {
        label: "Version control and observability",
        nav: [
          {
            label: "Git version control",
            path: "/git-sync",
          },
          {
            label: "dbt observability layer",
            path: "/dbt-observability",
          },
        ],
      },
    ],
  },
  {
    label: "Workspace management",
    path: "/workspace-management",
    nav: [
      {
        label: "Access control",
        nav: [
          {
            label: "Overview",
            path: "/overview",
          },
          {
            label: "Managing users and groups",
            path: "/user-management",
          },
          {
            label: "Roles",
            path: "/roles",
          },
          {
            label: "SSO setup guide",
            path: "/sso",
          },
        ],
      },
      {
        label: "Access control (legacy)",
        nav: [
          {
            label: "Overview",
            path: "/overview-legacy",
          },
          {
            label: "Role-Based controls",
            path: "/rbac",
          },
          {
            label: "Label-Based controls",
            path: "/lbac",
          },
          {
            label: "SSO setup guide",
            path: "/sso-legacy",
          },
        ],
      },
      {
        label: "Change management",
        nav: [
          {
            label: "Approval flows",
            path: "/approval-flows",
          },
          {
            label: "Environments",
            path: "/environments",
          },
          {
            label: "Audit logs",
            path: "/audit-logs",
          },
        ],
      },
      {
        label: "Organizing resources",
        nav: [
          {
            label: "Folders and labels",
            path: "/folders-filters",
          },
        ],
      },
    ],
  },
  {
    label: "Pricing",
    path: "/pricing",
    nav: [
      {
        label: "Self-serve pricing",
        path: "/ss-pricing",
      },
      {
        label: "Business tier pricing",
        path: "/bt-pricing",
      },
    ],
  },
  {
    label: "Security and compliance",
    path: "/security",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },

      {
        label: "Storage",
        path: "/storage",
      },

      {
        label: "Regions",
        path: "/regions",
      },

      {
        label: "Networking",
        path: "/networking",
        nav: [
          {
            label: "IP addresses",
            path: "/networking#ip-addresses",
          },
          {
            label: "SSH tunneling",
            path: "/ssh-tunneling",
          },
        ],
      },

      {
        label: "Amazon Web Services",
        nav: [
          {
            label: "Overview",
            path: "/aws",
          },

          {
            label: "AWS PrivateLink",
            path: "/aws/privatelink",
          },
        ],
      },

      {
        label: "Google Cloud",
        nav: [
          {
            label: "Overview",
            path: "/gcp",
          },
          {
            label: "Private Service Connect",
            path: "/gcp/private-service-connect",
          },
        ],
      },

      {
        label: "Microsoft Azure",
        nav: [
          {
            label: "Overview",
            path: "/azure",
          },
          {
            label: "Private Link",
            path: "/azure/private-link",
          },
        ],
      },
    ],
  },
  {
    label: "Developer tools",
    path: "/developer-tools",
    nav: [
      {
        label: "Personalization API",
        href: "/destinations/personalization-api",
      },
      {
        label: "REST API",
        nav: [
          {
            label: "API overview",
            path: "/api-guide",
          },
          {
            label: "API reference",
            href: "/api-reference",
          },
        ],
      },
      {
        label: "Command Line Interface",
        nav: [
          {
            label: "CLI overview",
            path: "/cli-guide",
          },
          {
            label: "CLI reference",
            path: "/cli-reference",
          },
        ],
      },
    ],
  },
];
